
import React, { useState } from 'react';
import { 
  Package,
  QrCode,
  Check,
  X,
  Search,
  Eye,
  Clock,
  MapPin,
  User,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Mock data for incoming shipments
const mockIncomingShipments = [
  {
    id: 'BAG001',
    type: 'bag',
    seatCode: 'ST001',
    fromHub: 'Bưu cục Hà Nội',
    driver: 'Trần Văn B',
    licensePlate: '29A-12345',
    totalOrders: 3,
    orders: ['DH001', 'DH002', 'DH003'],
    arrivedAt: '2025-07-12T14:30:00+07:00',
    status: 'Chờ nhập kho'
  },
  {
    id: 'DH004',
    type: 'order',
    fromHub: 'Bưu cục TP.HCM',
    driver: 'Phạm Văn D',
    licensePlate: '51B-67890',
    sender: 'Nguyễn Văn E',
    recipient: 'Lê Thị F',
    arrivedAt: '2025-07-12T13:15:00+07:00',
    status: 'Chờ nhập kho'
  }
];

// Mock data for warehouse inventory
const mockWarehouseInventory = [
  {
    id: 'DH001',
    type: 'order',
    sender: 'Nguyễn Văn A',
    recipient: 'Trần Thị B',
    address: 'Quận 1, TP.HCM',
    codAmount: 500000,
    receivedAt: '2025-07-12T14:30:00+07:00',
    status: 'Đã nhập kho',
    bagId: 'BAG001'
  },
  {
    id: 'DH002',
    type: 'order',
    sender: 'Lê Văn C',
    recipient: 'Phạm Thị D',
    address: 'Quận 3, TP.HCM',
    codAmount: 300000,
    receivedAt: '2025-07-12T14:30:00+07:00',
    status: 'Đã nhập kho',
    bagId: 'BAG001'
  }
];

const Warehouse = () => {
  const [activeTab, setActiveTab] = useState('incoming');
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inventoryMode, setInventoryMode] = useState(false);
  const [scannedOrders, setScannedOrders] = useState([]);
  const [scanInput, setScanInput] = useState('');

  const filteredIncoming = mockIncomingShipments.filter(shipment =>
    shipment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shipment.fromHub.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInventory = mockWarehouseInventory.filter(item =>
    item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.recipient.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Chờ nhập kho':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Đã nhập kho':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đã nhập kho có kiểm kê':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const handleReceiveWithoutInventory = () => {
    console.log('Receiving without inventory:', selectedShipment);
    setShowReceiveDialog(false);
    setSelectedShipment(null);
  };

  const handleReceiveWithInventory = () => {
    setInventoryMode(true);
    setScannedOrders([]);
  };

  const handleScanOrder = () => {
    if (scanInput.trim()) {
      const orderId = scanInput.trim();
      const isValidOrder = selectedShipment?.orders?.includes(orderId);
      
      if (isValidOrder && !scannedOrders.includes(orderId)) {
        setScannedOrders([...scannedOrders, orderId]);
      }
      setScanInput('');
    }
  };

  const handleSimulateScan = () => {
    // Simulate QR scan
    if (selectedShipment?.orders?.length > 0) {
      const remainingOrders = selectedShipment.orders.filter(order => !scannedOrders.includes(order));
      if (remainingOrders.length > 0) {
        const randomOrder = remainingOrders[Math.floor(Math.random() * remainingOrders.length)];
        setScannedOrders([...scannedOrders, randomOrder]);
      }
    }
  };

  const handleConfirmInventory = () => {
    console.log('Confirming inventory:', {
      shipment: selectedShipment,
      scannedOrders,
      totalOrders: selectedShipment?.totalOrders || 0
    });
    setShowReceiveDialog(false);
    setInventoryMode(false);
    setScannedOrders([]);
    setSelectedShipment(null);
  };

  const inventoryProgress = selectedShipment 
    ? (scannedOrders.length / (selectedShipment.totalOrders || 1)) * 100 
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Quản lý nhập kho</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="incoming" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Hàng đến ({mockIncomingShipments.length})
          </TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Hàng trong kho ({mockWarehouseInventory.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab hàng đến */}
        <TabsContent value="incoming">
          <Card className="animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Package className="h-5 w-5 mr-2 text-primary" />
                  Hàng cần nhập kho
                </CardTitle>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    placeholder="Tìm kiếm..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredIncoming.map((shipment) => (
                  <div key={shipment.id} className="p-4 border rounded-lg hover:shadow-md transition-all duration-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-primary/10 p-2 rounded-lg">
                          <Package className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">
                            {shipment.type === 'bag' ? `Bao ${shipment.id}` : `Đơn ${shipment.id}`}
                          </h3>
                          {shipment.seatCode && (
                            <p className="text-sm text-muted-foreground">SEAT: {shipment.seatCode}</p>
                          )}
                        </div>
                      </div>
                      <Badge className={getStatusColor(shipment.status)}>
                        {shipment.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>Từ: {shipment.fromHub}</span>
                        </div>
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>{shipment.driver} - {shipment.licensePlate}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>Đến: {new Date(shipment.arrivedAt).toLocaleString('vi-VN')}</span>
                        </div>
                        {shipment.totalOrders && (
                          <div className="flex items-center">
                            <Package className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span className="font-medium text-primary">
                              {shipment.totalOrders} đơn hàng
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-end mt-3 pt-3 border-t space-x-2">
                      {shipment.type === 'bag' && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4 mr-1" />
                              Xem đơn
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Danh sách đơn hàng - {shipment.id}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-2">
                              {shipment.orders.map((order, index) => (
                                <div key={index} className="p-3 border rounded-lg">
                                  <span className="font-mono">{order}</span>
                                </div>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      
                      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => setSelectedShipment(shipment)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Nhập kho
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>
                              Nhập kho - {selectedShipment?.type === 'bag' ? `Bao ${selectedShipment?.id}` : `Đơn ${selectedShipment?.id}`}
                            </DialogTitle>
                          </DialogHeader>
                          
                          {!inventoryMode ? (
                            <div className="space-y-4">
                              <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                  Bạn có muốn kiểm kê hàng hóa trước khi nhập kho không?
                                </AlertDescription>
                              </Alert>
                              
                              {selectedShipment?.type === 'bag' && (
                                <div className="border rounded-lg p-4">
                                  <h4 className="font-medium mb-2">Thông tin bao hàng</h4>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p><strong>Mã bao:</strong> {selectedShipment.id}</p>
                                      <p><strong>SEAT:</strong> {selectedShipment.seatCode}</p>
                                      <p><strong>Từ:</strong> {selectedShipment.fromHub}</p>
                                    </div>
                                    <div>
                                      <p><strong>Tài xế:</strong> {selectedShipment.driver}</p>
                                      <p><strong>Biển số:</strong> {selectedShipment.licensePlate}</p>
                                      <p><strong>Số đơn:</strong> {selectedShipment.totalOrders}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>
                                  Hủy
                                </Button>
                                <Button variant="outline" onClick={handleReceiveWithoutInventory}>
                                  Không kiểm kê
                                </Button>
                                <Button onClick={handleReceiveWithInventory}>
                                  Có kiểm kê
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <Alert>
                                <CheckCircle className="h-4 w-4" />
                                <AlertDescription>
                                  Quét mã từng đơn hàng để kiểm kê. Tiến độ: {scannedOrders.length}/{selectedShipment?.totalOrders || 0}
                                </AlertDescription>
                              </Alert>
                              
                              <Progress value={inventoryProgress} className="h-2" />
                              
                              <div className="flex space-x-2">
                                <Input
                                  placeholder="Nhập mã đơn hàng hoặc quét QR"
                                  value={scanInput}
                                  onChange={(e) => setScanInput(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleScanOrder()}
                                  className="flex-1"
                                />
                                <Button onClick={handleScanOrder} variant="outline">
                                  Thêm
                                </Button>
                                <Button onClick={handleSimulateScan} variant="outline">
                                  <QrCode className="h-4 w-4 mr-2" />
                                  Quét
                                </Button>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div className="border rounded-lg p-4">
                                  <h4 className="font-medium mb-2 text-green-600">Đã quét ({scannedOrders.length})</h4>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {scannedOrders.map((order, index) => (
                                      <div key={index} className="flex items-center space-x-2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                        <span className="font-mono text-sm">{order}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                
                                <div className="border rounded-lg p-4">
                                  <h4 className="font-medium mb-2 text-gray-600">Chưa quét</h4>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedShipment?.orders?.filter(order => !scannedOrders.includes(order)).map((order, index) => (
                                      <div key={index} className="flex items-center space-x-2">
                                        <AlertCircle className="h-4 w-4 text-gray-400" />
                                        <span className="font-mono text-sm text-gray-500">{order}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setInventoryMode(false)}>
                                  Quay lại
                                </Button>
                                <Button 
                                  onClick={handleConfirmInventory}
                                  disabled={scannedOrders.length === 0}
                                >
                                  Xác nhận nhập kho ({scannedOrders.length}/{selectedShipment?.totalOrders || 0})
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab hàng trong kho */}
        <TabsContent value="inventory">
          <Card className="animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                  Hàng trong kho
                </CardTitle>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    placeholder="Tìm kiếm..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredInventory.map((item) => (
                  <div key={item.id} className="p-4 border rounded-lg hover:shadow-md transition-all duration-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <Package className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{item.id}</h3>
                          {item.bagId && (
                            <p className="text-sm text-muted-foreground">Từ bao: {item.bagId}</p>
                          )}
                        </div>
                      </div>
                      <Badge className={getStatusColor(item.status)}>
                        {item.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>Gửi: {item.sender}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>Nhận: {item.recipient}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span>Địa chỉ: {item.address}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium text-primary">
                            COD: {item.codAmount.toLocaleString()}đ
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-sm text-muted-foreground">
                        Nhập kho: {new Date(item.receivedAt).toLocaleString('vi-VN')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Warehouse;
